import React, { useState, useEffect, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import viewImage from "../../assets/images/icons/view.svg";
import Profile from "../../assets/images/profile.png";
import BlockIcon from "../../assets/images/icons/block-icon.svg";
import FilterIcon from "../../assets/images/icons/filter-icon.svg";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS, getOrderStatusLabel, ROUTES } from "../../utils/constants";
import DateRangeFilterMenu from "../../commons/DateRangeFilterMenu/DateRangeFilterMenu";
import TablePagination from "../../commons/Pagination/TablePagination";
import { FaSyncAlt } from "react-icons/fa";
import {
  LIST_RETURN_KEYS,
  persistListReturn,
  consumeListReturn,
} from "../../utils/listNavigation";

const formatDob = (dob) => {
  if (!dob) return "-";
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return dob;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const parseAllergieList = (responseData) => {
  const payload = responseData?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getAllergyDisplayName = (entry, nameMap) => {
  const ref = entry?.allergy_id;
  if (ref && typeof ref === "object") {
    return ref.name || nameMap[ref._id] || null;
  }
  const id = typeof ref === "string" ? ref : ref?._id;
  return id ? nameMap[id] || null : null;
};

const formatCustomerAllergies = (allergies, nameMap) => {
  if (!allergies?.length) return "-";
  const names = allergies
    .map((item) => getAllergyDisplayName(item, nameMap))
    .filter(Boolean);
  return names.length ? names.join(", ") : "-";
};

function ViewCustomer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [allergyNameMap, setAllergyNameMap] = useState({});

  const formatOrderDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCustomerDetails = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await handleApiCall("get", `${endpoints.customerDetails}/${id}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setCustomerData(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomerDetails();
  }, [id]);

  useEffect(() => {
    const fetchAllergyNames = async () => {
      try {
        const response = await handleApiCall(
          "get",
          `${endpoints.allergiesList}?page=1&limit=100`
        );
        if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
          const list = parseAllergieList(response?.data);
          const map = list.reduce((acc, item) => {
            if (item?._id && item?.name) acc[item._id] = item.name;
            return acc;
          }, {});
          setAllergyNameMap(map);
        }
      } catch (error) {
        console.error("Error fetching allergy names:", error);
      }
    };

    fetchAllergyNames();
  }, []);

  const getCustomerOrders = useCallback(async () => {
    if (!id) return;

    setOrdersLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (appliedStartDate && appliedEndDate) {
        params.set("start_date", appliedStartDate);
        params.set("end_date", appliedEndDate);
      }

      const response = await handleApiCall("get", `${endpoints.customerOrders}/${id}?${params}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data;
        const list = Array.isArray(payload) ? payload : payload?.orders || payload?.data || [];
        setOrders(list);
        setTotalPages(payload?.pagination?.totalPages || payload?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching customer orders:", error);
    } finally {
      setOrdersLoading(false);
    }
  }, [id, page, limit, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    getCustomerOrders();
  }, [getCustomerOrders]);

  const handleApplyDateFilter = (start, end) => {
    setAppliedStartDate(start);
    setAppliedEndDate(end);
    setPage(1);
    setOpen(false);
  };

  const handleResetDateFilter = () => {
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setPage(1);
    setOpen(false);
  };

  useEffect(() => {
    if (location.state?.from || location.state?.listState) {
      persistListReturn(
        LIST_RETURN_KEYS.CUSTOMER.from,
        LIST_RETURN_KEYS.CUSTOMER.state,
        location.state?.from,
        location.state?.listState
      );
    }
  }, [location.state]);

  const handleBack = () => {
    const { from, listState } = consumeListReturn(
      LIST_RETURN_KEYS.CUSTOMER.from,
      LIST_RETURN_KEYS.CUSTOMER.state,
      location.state
    );

    navigate(from || ROUTES.CUSTOMER_MANAGEMENT, {
      state: listState ? { listState } : undefined,
    });
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">View User</h3>
            </div>
            <div>
              <div className="mt-3">
                <button type="button" className="btn btn-white rounded-lg" onClick={handleBack}>
                  Back
                </button>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-2 text-white">Loading customer details...</p>
                </div>
              ) : customerData ? (
                <>
                  <div className="profile-card mt-n5 pt-3 text-white">
                    <img
                      src={customerData?.image || Profile}
                      alt="Profile Picture"
                      className="profile-img mb-3"
                      onError={(e) => {
                        e.target.src = Profile;
                      }}
                    />
                    <h4 className="fw-bold mb-0">
                      {customerData?.full_name || "N/A"}
                      {/* <small className="text-muted fw-normal">(Offline)</small> */}
                    </h4>
                    {/* <div>
                      <small className="text-muted">(Registered)</small>
                    </div> */}
                    <button
                      type="button"
                      className={`btn status-badge-btn ${Number(customerData?.status) === 1 ? "btn-success" : "btn-secondary"} mt-2 rounded-lg px-3`}
                      disabled
                      aria-disabled="true"
                    >
                      <img src={BlockIcon} className="mr-2" alt="Status" />
                      {Number(customerData?.status) === 1 ? "Active" : "Inactive"}
                    </button>
                  </div>
                  <div className="border rounded-14 py-2 px-3 mt-5">
                    <div className="row mx-n2">
                      <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>Mobile Number</strong>
                          <p className="mb-0 mt-2">{customerData?.country_code ? `+${customerData.country_code} ${customerData.phone}` : customerData?.phone || "N/A"}</p>
                        </div>
                      </div>
                      <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>Email id</strong>
                          <p className="mb-0 mt-2">{customerData?.email || "N/A"}</p>
                        </div>
                      </div>
                      <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>Gender</strong>
                          <p className="mb-0 mt-2">{customerData?.gender || "N/A"}</p>
                        </div>
                      </div>
                      <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>Date of Birth</strong>
                          <p className="mb-0 mt-2">{formatDob(customerData?.dob)}</p>
                        </div>
                      </div>
                      <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>Allergies</strong>
                          <p className="mb-0 mt-2 text-wrap">
                            {formatCustomerAllergies(customerData?.allergies, allergyNameMap)}
                          </p>
                        </div>
                      </div>
                      {/* <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>Profile Complete</strong>
                          <p className="mb-0 mt-2">{customerData?.is_profile_complete ? "Yes" : "No"}</p>
                        </div>
                      </div> */}
                      {/* <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>OTP Verified</strong>
                          <p className="mb-0 mt-2">{customerData?.verify_otp ? "Yes" : "No"}</p>
                        </div>
                      </div> */}
                      <div className="col-md-4 p-2">
                        <div className="rounded-14 border p-3 text-white">
                          <strong>Created Date</strong>
                          <p className="mb-0 mt-2">{customerData?.createdAt ? new Date(customerData.createdAt).toLocaleDateString() : "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <p className="text-white">Customer not found or failed to load.</p>
                </div>
              )}

              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0 text-white">Customer Orders</h4>
                  <div className="d-flex align-items-center ml-auto">
                    <button
                      type="button"
                      className="btn btn-default rounded-lg mr-2 d-flex align-items-center justify-content-center"
                      onClick={() => getCustomerOrders()}
                      disabled={ordersLoading}
                      title="Reload"
                      aria-label="Reload"
                    >
                      <FaSyncAlt />
                    </button>
                    <div className="dropdown text-end w-auto mr-1 bg-white">
                      <img src={FilterIcon} onClick={() => setOpen(!open)} alt="Filter" />
                      <div className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${open ? "show" : ""}`} style={{ top: "-150px", right: "40px" }}>
                        <DateRangeFilterMenu
                          isOpen={open}
                          appliedFrom={appliedStartDate}
                          appliedTo={appliedEndDate}
                          onApply={handleApplyDateFilter}
                          onReset={handleResetDateFilter}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="150">Order ID</th>
                          <th width="150">Restaurant</th>
                          <th>Date</th>
                          <th>Order Status</th>
                          <th>Total Amount</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordersLoading ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        ) : orders.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-white">
                              {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                            </td>
                          </tr>
                        ) : (
                          orders.map((order) => (
                            <tr key={order._id}>
                              <td className="text-left">#{order.order_number || "N/A"}</td>
                              <td>{order.restaurant_id?.restaurant_name || order.restaurant_name || "N/A"}</td>
                              <td>{formatOrderDate(order.createdAt)}</td>
                              <td>{getOrderStatusLabel(order.order_status)}</td>
                              <td>£{order.amount_to_pay ?? order.total_amount ?? "0"}</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  <Link
                                    to={`/view-order/${order._id}`}
                                    state={{
                                      from: `${ROUTES.VIEW_CUSTOMER}/${id}`,
                                      listState: location.state?.listState,
                                      customerListFrom: location.state?.from,
                                    }}
                                    className="mx-2"
                                  >
                                    <img src={viewImage} alt="View" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewCustomer;
